/**
 * CP2.2 — ShipToAddressPanel
 *
 * Composes ShipToAddressForm + ShippingQuotePicker and drives the
 * `useShipToAddressFlow` hook.
 *
 * Contract with parent:
 *   * `onSelectionChange(selection | null)` — parent persists selection
 *     as the customer's chosen quote in checkout state. Selection is
 *     wiped automatically whenever the cart/address/mode change.
 *   * The parent controls when to render this panel (only when
 *     `fulfillmentMode === "shipping"`).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import ShipToAddressForm from "./ShipToAddressForm";
import ShippingQuotePicker from "./ShippingQuotePicker";
import { useShipToAddressFlow, fingerprintAddress } from "../../../hooks/useShipToAddressFlow";
import { toValidateAddressBody, suggestionDiffersFromSubmitted } from "../../../lib/shipToAddressValidation";
import { logCustomerAuditEvent } from "../../../lib/customerAuditLog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function defaultApiPost(path, body, { signal } = {}) {
  const url = `${API}${path}`;
  try {
    const res = await axios.post(url, body, { signal, timeout: 20000 });
    return res.data;
  } catch (err) {
    if (err?.response?.status === 429) {
      return { ok: false, detail: { code: "rate_limited", detail: "Too many requests" } };
    }
    if (err?.response?.data) return err.response.data;
    const msg = err?.message || "network_error";
    throw Object.assign(new Error(msg), { code: "network_error" });
  }
}

export default function ShipToAddressPanel({
  slug,
  cart,
  fulfillmentMode,
  catalogId,
  sessionId,
  initialAddress,
  onAddressChange,
  onSelectionChange,
  apiPost,
}) {
  // Parcel Shipping Readiness & Checkout Completion — Phase 4. Seed from
  // whatever the parent already knows (a previously-entered shipping
  // address for THIS session, or at minimum the name/email/phone already
  // captured at Welcome) instead of always starting blank. The parent
  // (RetailCustomer.jsx) persists this in the same session-storage
  // `customer` record CheckoutModal already reads, so it survives this
  // panel — and the whole CheckoutModal — unmounting when the customer
  // closes checkout to keep shopping and reopens it later.
  const [address, setAddress] = useState(initialAddress || null);
  const post = apiPost || defaultApiPost;
  const {
    state,
    selection,
    fetchQuotes,
    selectQuote,
    clearSelection,
  } = useShipToAddressFlow({
    slug,
    cart,
    fulfillmentMode,
    catalogId,
    sessionId,
    apiPost: post,
  });

  // Parcel Shipping Checkout Enforcement — a selected service must be
  // cleared the INSTANT the customer edits any address field, not only
  // after they resubmit the form. Without this, a stale selection (and
  // its stale charge) stayed visible/usable while the visible form no
  // longer matched what was actually quoted.
  const [addressDirty, setAddressDirty] = useState(false);
  const handleFieldChange = useCallback((liveForm) => {
    if (state.status === "idle") return;
    const dirty = fingerprintAddress(liveForm) !== state.addressFingerprint;
    setAddressDirty(dirty);
    if (dirty && selection) {
      clearSelection();
      logCustomerAuditEvent(slug, { event: "shipping_quote_invalidated", reasonCode: "address_edited", catalogId });
    }
  }, [state.status, state.addressFingerprint, selection, clearSelection, slug, catalogId]);

  // Phase 5 — address validation runs BEFORE the rate request, as its
  // own state machine distinct from `state.status` (the quote-fetch
  // state) so "this address looks wrong" and "the carrier couldn't
  // quote it" are never conflated into one error the customer can't
  // tell apart.
  //   idle | checking | needs_confirmation | error
  const [validation, setValidation] = useState({ status: "idle", pending: null, suggestion: null, message: "" });

  const proceedToRates = useCallback(async (addr) => {
    setAddress(addr);
    setAddressDirty(false);
    onAddressChange && onAddressChange(addr);
    logCustomerAuditEvent(slug, { event: "shipping_rate_request_started", catalogId, sessionId });
    await fetchQuotes(addr);
  }, [fetchQuotes, onAddressChange, slug, catalogId, sessionId]);

  // Observability — one `shipping_rates_returned` per completed request,
  // fired on the LOADING -> {READY|ERROR} transition (never on mount, and
  // never twice for the same response) so it always pairs with the
  // `shipping_rate_request_started` beacon above.
  const prevStatusRef = useRef(state.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = state.status;
    if (prev === "loading" && (state.status === "ready" || state.status === "error")) {
      logCustomerAuditEvent(slug, {
        event: "shipping_rates_returned",
        reasonCode: state.status === "error" ? (state.errorCode || undefined) : undefined,
        catalogId, sessionId,
        meta: state.status === "ready" ? { quote_count: (state.quotes || []).length } : undefined,
      });
    }
  }, [state.status, state.errorCode, state.quotes, slug, catalogId, sessionId]);

  // A quote that expires client-side (the customer sat on the page too
  // long) must be distinguishable in observability from one invalidated
  // by an address/cart edit — same underlying "selection cleared" effect,
  // different root cause.
  const quoteExpiredLoggedRef = useRef(false);
  useEffect(() => {
    if (state.status === "expired") {
      if (!quoteExpiredLoggedRef.current) {
        quoteExpiredLoggedRef.current = true;
        logCustomerAuditEvent(slug, { event: "shipping_quote_expired", catalogId, sessionId });
      }
    } else {
      quoteExpiredLoggedRef.current = false;
    }
  }, [state.status, slug, catalogId, sessionId]);

  const handleAddressSubmit = useCallback(async (addr) => {
    setValidation({ status: "checking", pending: addr, suggestion: null, message: "" });
    try {
      const result = await post(
        `/retail/public/${encodeURIComponent(slug)}/shipping/validate-address`,
        { ...toValidateAddressBody(addr), catalog_id: catalogId || null },
      );
      if (result?.detail?.code) {
        // Structured backend error (e.g. parcel_shipping_unavailable) —
        // don't block checkout on a validation-layer problem; fall
        // through to the rate request, which will surface its own
        // clearer error if shipping is genuinely unavailable.
        await proceedToRates(addr);
        return;
      }
      const suggestion = result?.suggestion || null;
      if (result?.ok !== false && suggestionDiffersFromSubmitted(addr, suggestion)) {
        setValidation({ status: "needs_confirmation", pending: addr, suggestion, message: (result.messages || [])[0] || "" });
        return;
      }
      if (result?.ok === false && !suggestion) {
        setValidation({
          status: "error", pending: addr, suggestion: null,
          message: (result.messages || [])[0] || "We couldn't verify this address. Please double-check it.",
        });
        return;
      }
      setValidation({ status: "idle", pending: null, suggestion: null, message: "" });
      await proceedToRates(addr);
    } catch (_e) {
      // Network/unexpected failure in the validation step itself must
      // never trap the customer — fall through to the rate request.
      setValidation({ status: "idle", pending: null, suggestion: null, message: "" });
      await proceedToRates(addr);
    }
  }, [post, slug, catalogId, proceedToRates]);

  const acceptSuggestion = useCallback(async () => {
    const merged = { ...(validation.pending || {}), ...(validation.suggestion || {}) };
    setValidation({ status: "idle", pending: null, suggestion: null, message: "" });
    await proceedToRates(merged);
  }, [validation, proceedToRates]);

  const keepOriginal = useCallback(async () => {
    const addr = validation.pending;
    setValidation({ status: "idle", pending: null, suggestion: null, message: "" });
    if (addr) await proceedToRates(addr);
  }, [validation, proceedToRates]);

  React.useEffect(() => {
    onSelectionChange && onSelectionChange(selection);
  }, [selection, onSelectionChange]);

  const retry = useCallback(() => {
    if (address) fetchQuotes(address);
  }, [address, fetchQuotes]);

  if (fulfillmentMode !== "shipping") return null;

  const showQuotes = state.status !== "idle" && validation.status !== "needs_confirmation" && !addressDirty;

  return (
    <div
      className="space-y-5 p-1 md:p-2"
      data-testid="ship-to-address-panel"
    >
      <ShipToAddressForm
        initial={address}
        onSubmit={handleAddressSubmit}
        onFieldChange={handleFieldChange}
        disabled={state.status === "loading" || validation.status === "checking"}
        slug={slug}
        catalogId={catalogId}
        sessionId={sessionId}
      />
      {addressDirty && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          role="status" data-testid="ship-address-dirty-hint"
        >
          Your address changed — select "Select shipping method" again to see current rates.
        </div>
      )}
      {validation.status === "error" && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          role="alert" data-testid="ship-address-validation-error"
        >
          {validation.message}
          <button
            type="button"
            data-testid="ship-address-validation-continue-anyway"
            className="ml-2 underline"
            onClick={() => proceedToRates(validation.pending)}
          >
            Use this address anyway
          </button>
        </div>
      )}
      {validation.status === "needs_confirmation" && (
        <div
          className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900 space-y-2"
          data-testid="ship-address-suggestion"
        >
          <div className="font-medium">We found a suggested address:</div>
          <div data-testid="ship-address-suggestion-text">
            {[validation.suggestion?.street1, validation.suggestion?.street2, validation.suggestion?.city, validation.suggestion?.region, validation.suggestion?.postal].filter(Boolean).join(", ")}
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" data-testid="ship-address-suggestion-accept" className="px-3 py-1.5 rounded-md bg-sky-600 text-white text-xs font-medium" onClick={acceptSuggestion}>
              Use suggested address
            </button>
            <button type="button" data-testid="ship-address-suggestion-keep" className="px-3 py-1.5 rounded-md border border-sky-300 text-xs font-medium" onClick={keepOriginal}>
              Keep as entered
            </button>
          </div>
        </div>
      )}
      {showQuotes && (
        <ShippingQuotePicker
          status={state.status}
          quotes={state.quotes}
          partialProviders={state.partialProviders}
          errorCode={state.errorCode}
          onRetry={retry}
          onSelect={selectQuote}
          selectedServiceCode={selection?.service_code}
        />
      )}
    </div>
  );
}
